import React, { useState, useEffect, useReducer, useRef, useMemo } from "react";
import {
  Grid,
  Box,
  Card,
  IconButton,
  Input,
  InputAdornment,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import { get } from "aws-amplify/api";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
  GridToolbarColumnsButton,
  GridRowModes,
  useGridApiRef,
} from "@mui/x-data-grid";
import { I18n } from "@aws-amplify/core";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import currentUserToken from "CurrentUserToken";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { post } from "aws-amplify/api";
import { APIStatusDialog } from "components/APIStatusDialog";
import { DataGridPro } from "@mui/x-data-grid-pro/DataGridPro";
import EncodingSettingsMenu from "../components/EncodingSettingsMenu";
import PatternSettingsMenu from "../components/PatternSettingsMenu";
import EventHistorySettingsMenu from "../components/EventHistorySettingsMenu";
import { itIT } from "@mui/x-data-grid/locales";
import { deDE } from "@mui/x-data-grid/locales";
import { CircularProgress } from "@mui/material";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};
function EventHistory() {
  const [state, dispatch] = useReducer(reducer, {
    audit: [],
    fetched: false,
  });
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [startDate, setStartDate] = useState(dayjs().startOf("day"));
  const [endDate, setEndDate] = useState(dayjs().endOf("day"));

  const [languages, setLanguages] = useState([]);
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  const toolbarRef = useRef(null);

  useEffect(() => {
    const parentElement = toolbarRef.current;
    if (!parentElement) {
      console.error("toolbarRef is not correctly attached to any DOM element.");
      return;
    }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        checkSize(entry.contentRect.width);
      }
    });
    resizeObserver.observe(parentElement);
    checkSize(parentElement.offsetWidth);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);
  const apiRef = useGridApiRef();
  const [searchText, setSearchText] = useState("");
  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "en"
  );
  const [resizeThrottle, setResizeThrottle] = useState(60);
  const [rowModesModel, setRowModesModel] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const handleRowSelection = (ids) => {
    setSelectedRows(ids);
  };

  useEffect(() => {
    const hiddenColumns = JSON.parse(
      localStorage.getItem("hiddenColumns") || "[]"
    );
    if (apiRef.current) {
      apiRef.current.setColumnVisibilityModel(
        hiddenColumns.reduce((model, field) => {
          model[field] = false;
          return model;
        }, {})
      );
    }
  }, [apiRef]);

  const handleColumnVisibilityChange = (newModel) => {
    const hiddenColumns = Object.keys(newModel).filter(
      (field) => !newModel[field]
    );
    localStorage.setItem("hiddenColumns", JSON.stringify(hiddenColumns));
  };

  const tlmGetEventHistory = async () => {
    try {
      const formattedStartDate = startDate.format("YYYY-MM-DD HH:mm:ss");
      const formattedEndDate = endDate.format("YYYY-MM-DD HH:mm:ss");
      setLoading(true);
      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/eventHistory/get",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            start_date: formattedStartDate,
            end_date: formattedEndDate,
          },
        },
      });

      const { body } = await restOperation.response;
      const response = await body.json();

      dispatch({ type: "DATA_RECEIVED", value: response });
      setLoading(false);
    } catch (error) {
      console.error("Errore durante la chiamata API:", error);
    }
  };

  useEffect(() => {
    tlmGetEventHistory();
  }, []);

  useEffect(() => {
    const tlmGetLanguagesAPI = async () => {
      try {
        const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/languages",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {},
          },
        });
        const { body } = await restOperation.response;
        const response = await body.json();

        setLanguages(response);
      } catch (error) {
        console.error("Errore durante la chiamata API:", error);
      }
    };
    tlmGetLanguagesAPI();
  }, []);
  const columns = useMemo(() => {
    return [
      {
        field: "executor",
        headerName: I18n.get("Executor"),
        width: 200,
        editable: true,
      },
      {
        field: "account",
        headerName: I18n.get("Account number"),
        width: 200,
        editable: true,
      },
      {
        field: "modified_unit_id",
        headerName: I18n.get("Unit ID"),
        width: 300,
        editable: true,
      },
      {
        field: "modified_unit",
        headerName: I18n.get("Unit"),
        width: 300,
        editable: true,
      },
      {
        field: "timestamp",
        headerName: I18n.get("Timestamp"),
        width: 300,
        editable: true,
        valueFormatter: (value) => {
          if (!value) {
            return ""; // Handle null or undefined values
          }
          const formattedDate = dayjs
            .utc(value)
            .tz(dayjs.tz.guess())
            .format("YYYY-MM-DD HH:mm:ss");
          return formattedDate;
        },
      },
      {
        field: "process",
        headerName: I18n.get("Process"),
        width: 300,
        editable: true,
      },
      {
        field: "old_value",
        headerName: I18n.get("Old Value"),
        width: 600,
        editable: true,
      },
      {
        field: "new_value",
        headerName: I18n.get("New Value"),
        width: 600,
        editable: true,
      },
    ];
  }, [rowModesModel]);

  const applyStoreFiltering = (value) => {
    let matchesAllFilters = true;
    if (searchText) {
      const searchTextLower = searchText.toLowerCase();
      matchesAllFilters =
        matchesAllFilters &&
        Object.values(value).some((val) =>
          String(val).toLowerCase().includes(searchTextLower)
        );
    }
    return matchesAllFilters;
  };
  const filteredRows = state.audit.filter(applyStoreFiltering);
  const filteredRowsCount = filteredRows.length;
  const searchChanged = (event) => {
    setSearchText(event.target.value);
  };

  const [loading, setLoading] = useState(false);
  const renderToolbar = () => {
    return (
      <div ref={toolbarRef}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          {" "}
          {/* Aggiunto mb per lo spazio sotto il titolo */}
          <Typography variant="h5" mt={0.5} mb={1}>
            {I18n.get("Event History")} (
            {filters ? filteredRowsCount : totalCount})
          </Typography>
        </Stack>
        <Stack
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          spacing={2}
          sx={{ pb: 2 }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={I18n.get("Start Date")}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
            <DatePicker
              label={I18n.get("End Date")}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
          </LocalizationProvider>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={() => tlmGetEventHistory()} // Calls the API with updated date values
          >
            {I18n.get("Search")}
          </Button>
          {loading && <CircularProgress size="1.5rem" />}
          <Box
            sx={{
              ml: 2,
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Input
              type="text"
              placeholder={I18n.get("Search")}
              onChange={searchChanged}
              fullWidth={tableParentIsMD}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Box>
        </Stack>
      </div>
    );
  };
  function getBrowserName() {
    const userAgent = navigator.userAgent;
    if (userAgent.includes("Firefox")) {
      return "Firefox";
    } else if (userAgent.includes("Edg")) {
      return "Edge";
    } else if (userAgent.includes("Chrome")) {
      return "Chrome";
    }
  }
  const browser = getBrowserName();
  const [tableHeight, setTableHeight] = useState(0);
  useEffect(() => {
    const updateHeight = () => {
      const navbarHeight = document.getElementById("navbar");
      const footerHeight = document.getElementById("footer");
      const margin =
        browser === "Firefox" ? 200 : browser === "Chrome" ? 200 : 200;
      const availableHeight =
        document.documentElement.clientHeight -
        navbarHeight -
        footerHeight -
        margin;
      setTableHeight(availableHeight);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);
  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);
  const darkMode = localStorage.getItem("darkMode") === "true";
  return (
    <>
      <DashboardLayout>
        <DashboardNavbar
          absolute
          light
          language={language}
          setLanguage={setLanguage}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: 3, mt: 3, width: "400px" }}
        >
          <EncodingSettingsMenu />
          <PatternSettingsMenu />
          <EventHistorySettingsMenu />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, maxHeight: `${tableHeight}px` }}>
              {" "}
              {/** lasciare così */}
              <Card
                sx={{
                  p: 3,
                  maxHeight: `${tableHeight}px`,
                }}
              >
                {renderToolbar()}
                <Box
                  sx={{ p: 2, height: `${tableHeight}px`, overflow: "auto" }}
                >
                  <DataGridPro
                    sx={{
                      "& .MuiDataGrid-root, & .MuiDataGrid-footerContainer": {
                        minHeight: "2rem",
                      },
                      "&  .MuiDataGrid-cell ": {
                        cursor: "default",
                      },
                      borderColor: darkMode ? "#6B635A" : "",
                      "& .MuiDataGrid-cell, &.MuiDataGrid-columnHeaders": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        borderColor: darkMode ? "#625a52" : "",
                      },
                      "--DataGrid-rowBorderColor": darkMode ? "#625a52" : "",
                      minHeight: "20vh",
                      flexFlow: 1,
                      height: "100%",
                    }}
                    onCellDoubleClick={(params, event) => {
                      // Prevent the default double-click behavior
                      event.defaultMuiPrevented = true;
                    }}
                    localeText={
                      language === "it"
                        ? itIT.components.MuiDataGrid.defaultProps.localeText
                        : language === "ge"
                        ? deDE.components.MuiDataGrid.defaultProps.localeText
                        : ""
                    }
                    rows={filteredRows}
                    columns={columns}
                    initialState={{
                      density: "compact",
                      pinnedColumns: { right: ["action"] },
                    }}
                    disableRowSelectionOnClick
                    getRowId={(row) => row.id}
                    rowModesModel={rowModesModel}
                    resizeThrottleMs={resizeThrottle}
                    loading={!state.fetched || resizeThrottle > 60}
                    onRowModesModelChange={(newModel) =>
                      setRowModesModel(newModel)
                    }
                    onRowSelectionModelChange={(newSelection) =>
                      handleRowSelection(newSelection)
                    }
                    apiRef={apiRef}
                    onColumnVisibilityModelChange={handleColumnVisibilityChange}
                    onSelectionModelChange={(newSelection) => {
                      const selectedRows = newSelection.map((id) =>
                        filteredRows.find((row) => row.id === id)
                      );
                    }}
                    pagination 
                    hideFooterRowCount
                    autoPageSize
                    slotProps={{
                      loadingOverlay: {
                        variant: "skeleton",
                        noRowsVariant: "skeleton",
                      },
                    }}
                    slots={{
                      toolbar: () => (
                        <GridToolbarContainer>
                          <GridToolbarColumnsButton />
                          <GridToolbarExport
                            printOptions={{ disableToolbarButton: true }}
                            csvOptions={{
                              fileName: "Event History",
                              utf8WithBom: true,
                            }}
                          />
                          <GridToolbarFilterButton />
                        </GridToolbarContainer>
                      ),
                    }}
                  />
                </Box>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DashboardLayout>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}

export default EventHistory;
